import classNames from "classnames";
import { useState } from "react";
import HandleClicksOutside from "../HandleClicksOutside";
import "./Dropdown.css";
import { AiCapturedBadge } from "@prototypes/ai-capture/AiCapturedBadge";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { Flex } from "./Flex";
import Icon from "./Icon";

export type DropdownOption = {
    value: string | null;
    label: string;
    image?: string | React.JSX.Element;
};

type Props = {
    optionList?: DropdownOption[];
    entries?: [string, string][];
    label?: string;
    currentValue?: string | null | undefined;
    defaultLabel?: string;
    className?: string;
    onChange: (value: string | null) => Promise<any> | void;
    isInvalidInput?: boolean;
    highlight?: boolean;
};

const Dropdown = function Dropdown(props: Props) {
    const {
        optionList,
        entries,
        label,
        currentValue,
        defaultLabel,
        className,
        onChange,
        isInvalidInput = false,
        highlight = false,
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const options =
        optionList ||
        entries?.map<DropdownOption>(([key, value]) => ({
            value: key,
            label: value,
        }));
    if (!options) return <div className="dropdown" />;

    const selected = options.filter((o) => o.value === currentValue)[0] || options[0];

    const renderImage = (image: string | React.JSX.Element) => {
        if (!image) return "";
        if (typeof image === "string") {
            return <img src={image} alt="" />;
        }
        return image;
    };

    const triggerOnChange = async (optionValue: string | null) => {
        setIsSaving(true);
        await onChange(optionValue);
        setIsSaving(false);
    };

    return (
        <HandleClicksOutside
            className={classNames(
                className,
                "dropdown__container",
                isInvalidInput ? "invalid-input" : "",
            )}
            onClickOutside={() => {
                setIsOpen(false);
            }}
            disabled={!isOpen}
        >
            <div
                className={classNames("dropdown", {
                    "dropdown--open": isOpen,
                    "!border-blue-200 !bg-blue-50": highlight,
                })}
            >
                <Flex.Row
                    className={classNames("dropdown__current", {
                        "dropdown__current--is-saving": isSaving,
                        "!pr-1": highlight,
                    })}
                    onClick={() => {
                        setIsOpen(!isOpen);
                    }}
                >
                    <Flex.Column gap="xsmall">
                        {label && <label className="dropdown__label">{label}</label>}
                        <button type="button">
                            {selected?.image && (
                                <span className="dropdown__image">
                                    {renderImage(selected.image)}
                                </span>
                            )}
                            <span className="dropdown__title">
                                {defaultLabel ||
                                    (selected && (selected.label || selected.value))}
                            </span>
                        </button>
                    </Flex.Column>
                    <span className="dropdown__triangle">
                        {isOpen ? <Icon.ExpandLess.Small /> : <Icon.ExpandMore.Small />}
                    </span>
                    {highlight && <AiCapturedBadge inline />}
                </Flex.Row>
                {isSaving && (
                    <div className="dropdown__loading-spinner">
                        <LoadingSpinner />
                    </div>
                )}
                {isOpen && (
                    <div className="dropdown__options">
                        {options.map((option) => (
                            <div
                                className="dropdown__option"
                                key={`dropdown-${option.value}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        const optionValue =
                                            option.value === "" ? null : option.value;
                                        triggerOnChange(optionValue);
                                        setIsOpen(false);
                                    }}
                                >
                                    {option.image && (
                                        <span className="dropdown__image">
                                            {renderImage(option.image)}
                                        </span>
                                    )}
                                    <span>{option.label || option.value}</span>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </HandleClicksOutside>
    );
};

export default Dropdown;
